/**
 * Phase 7-lite — Checkout Broken Diagnostic Report.
 *
 * Read-only audit page that lists every `/api/orders` attempt that failed,
 * grouped by error code with a 7-day summary. Helps operators triage
 * checkout breakage without trawling logs. Regional dispatchers see only
 * their region; super_admin sees all.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { ArrowLeft, RefreshCw, AlertTriangle, Filter, Mail } from 'lucide-react';
import { SendDirectEmailModal } from '../../components/SendDirectEmailModal';
import { Navbar } from '../../components/Navbar';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Map a failed-order error code to the most appropriate email template id.
// Codes without a specific match fall through to the apology-bug template.
const CODE_TO_TEMPLATE = {
  REGION_NOT_DELIVERABLE: 'out_of_region',
  // OUT_OF_STOCK / VARIANT_NOT_CONFIGURED / PRODUCT_NOT_FOUND / STOCK_RACE /
  // INSUFFICIENT_POINTS / LOYALTY_DISABLED / INVALID_REDEMPTION / SERVER_ERROR
  // → fall through to the apology-bug default.
};

const CODE_COLORS = {
  OUT_OF_STOCK: 'text-amber-400 border-amber-400/40 bg-amber-400/10',
  OUT_OF_STOCK_VARIANT: 'text-amber-400 border-amber-400/40 bg-amber-400/10',
  VARIANT_NOT_CONFIGURED: 'text-red-400 border-red-400/40 bg-red-400/10',
  PRODUCT_NOT_FOUND: 'text-red-400 border-red-400/40 bg-red-400/10',
  REGION_NOT_DELIVERABLE: 'text-cyan-400 border-cyan-400/40 bg-cyan-400/10',
  STOCK_RACE: 'text-purple-400 border-purple-400/40 bg-purple-400/10',
  INSUFFICIENT_POINTS: 'text-cyan-400 border-cyan-400/40 bg-cyan-400/10',
  LOYALTY_DISABLED: 'text-cyan-400 border-cyan-400/40 bg-cyan-400/10',
  INVALID_REDEMPTION: 'text-cyan-400 border-cyan-400/40 bg-cyan-400/10',
  SERVER_ERROR: 'text-pink-400 border-pink-400/40 bg-pink-400/10',
};

const codeClass = (c) => CODE_COLORS[c] || 'text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/10';

const formatTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function FailedOrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [openNotify, setOpenNotify] = useState(null); // { customer, context }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const q = codeFilter ? `?code=${encodeURIComponent(codeFilter)}&limit=200` : '?limit=200';
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/failed-orders${q}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/failed-orders/summary`, { withCredentials: true }),
      ]);
      setRows(listRes.data || []);
      setSummary(sumRes.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load failed orders');
    } finally {
      setLoading(false);
    }
  }, [codeFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="min-h-screen bg-[#050003] text-white dispatch-safe-top" data-testid="failed-orders-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/dispatch')}
              className="text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none"
              data-testid="back-to-dispatch"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> BACK
            </Button>
            <div>
              <h1 className="text-[#39FF14] font-mono text-2xl font-bold flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                CHECKOUT_BROKEN_REPORT
              </h1>
              <p className="text-[#4B6B52] font-mono text-sm">
                // Every failed /api/orders attempt — last 7 days summarized
                {user?.role === 'dispatcher' && user?.region ? ` · region=${user.region}` : ' · all regions'}
              </p>
            </div>
          </div>
          <Button
            onClick={fetchAll}
            disabled={loading}
            className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none font-mono"
            data-testid="refresh-failed-orders"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            REFRESH
          </Button>
        </div>

        {/* 7-day summary */}
        <div className="border border-[#39FF14]/30 bg-[#0C0612] p-4 mb-6">
          <div className="font-mono text-xs text-[#4B6B52] mb-3">
            7-DAY SUMMARY · TOTAL FAILED: <span className="text-[#39FF14]">{summary?.total ?? 0}</span>
          </div>
          {(summary?.by_code || []).length === 0 ? (
            <div className="font-mono text-sm text-[#4B6B52]">
              {'// no failed checkouts in the last 7 days — 👌'}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCodeFilter('')}
                className={`px-3 py-1 border font-mono text-xs ${codeFilter === '' ? 'border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10' : 'border-[#4B6B52] text-[#4B6B52] hover:text-[#39FF14] hover:border-[#39FF14]'}`}
                data-testid="filter-all"
              >
                ALL ({summary?.total || 0})
              </button>
              {(summary?.by_code || []).map((r) => (
                <button
                  key={r.code}
                  onClick={() => setCodeFilter(r.code === 'UNKNOWN' ? '' : r.code)}
                  className={`px-3 py-1 border font-mono text-xs ${codeFilter === r.code ? codeClass(r.code) : 'border-[#4B6B52] text-[#4B6B52] hover:text-white'}`}
                  data-testid={`filter-${r.code}`}
                >
                  {r.code} · {r.count}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Listing */}
        <div className="border border-[#39FF14]/20 bg-[#050003]">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#39FF14]/20 bg-[#0C0612]">
            <div className="font-mono text-xs text-[#4B6B52] flex items-center gap-2">
              <Filter className="w-3 h-3" />
              SHOWING {rows.length} {codeFilter ? `· ${codeFilter}` : '· all codes'}
            </div>
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-center font-mono text-[#4B6B52]">
              {'// no failed orders match the current filter'}
            </div>
          ) : (
            <div className="divide-y divide-[#39FF14]/10">
              {rows.map((r) => (
                <div key={r.id} className="px-4 py-3 hover:bg-[#0C0612]/50" data-testid={`failed-row-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`px-2 py-0.5 border font-mono text-xs ${codeClass(r.code)}`}>
                          {r.code || 'UNKNOWN'}
                        </span>
                        {r.region && (
                          <span className="px-2 py-0.5 border border-[#4B6B52] text-[#4B6B52] font-mono text-xs">
                            {String(r.region).toUpperCase()}
                          </span>
                        )}
                        <span className="font-mono text-xs text-[#4B6B52]">
                          {formatTime(r.created_at)}
                        </span>
                      </div>
                      <div className="font-mono text-sm text-white break-words">
                        {r.reason}
                      </div>
                      <div className="font-mono text-xs text-[#4B6B52] mt-1">
                        {r.customer_id ? (
                          <Link
                            to={`/dispatch/admin/users?openUser=${r.customer_id}&from=failed-orders`}
                            className="text-white/70 hover:text-[#39FF14] hover:underline"
                            data-testid={`failed-order-customer-link-${r.id}`}
                          >
                            {r.customer_name || '—'}
                          </Link>
                        ) : (
                          <span className="text-white/70">{r.customer_name || '—'}</span>
                        )}
                        {' · '}
                        <span>{r.customer_email || '—'}</span>
                        {r.product_name && (
                          <>
                            {' · product='}
                            <span className="text-white/70">{r.product_name}</span>
                          </>
                        )}
                        {r.size && (
                          <>
                            {' · size='}
                            <span className="text-white/70">{r.size}</span>
                          </>
                        )}
                      </div>
                      <details className="mt-1">
                        <summary className="font-mono text-xs text-[#39FF14]/60 hover:text-[#39FF14] cursor-pointer">
                          payload &amp; address
                        </summary>
                        <div className="mt-1 p-2 border border-[#39FF14]/10 bg-[#0C0612] font-mono text-xs text-[#4B6B52] whitespace-pre-wrap break-words">
                          {`address: ${r.delivery_address || '—'}\npayment: ${r.payment_method || '—'}\nredeem_pts: ${r.redeem_pts || 0}\nitems: ${JSON.stringify(r.items || [], null, 2)}`}
                        </div>
                      </details>
                    </div>
                    {/* Notify button — opens shared modal with template hinted by failure code */}
                    {r.customer_email && (
                      <button
                        onClick={() => setOpenNotify({
                          customer: {
                            id: r.customer_id,
                            email: r.customer_email,
                            name: r.customer_name,
                          },
                          context: {
                            template_id: CODE_TO_TEMPLATE[r.code],
                          },
                        })}
                        className="px-3 py-2 text-xs uppercase tracking-widest border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] flex items-center gap-1.5 shrink-0 self-start"
                        data-testid={`failed-order-notify-${r.id}`}
                        title={`Email ${r.customer_email} about this failed order`}
                      >
                        <Mail size={12} />
                        NOTIFY
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {openNotify && (
        <SendDirectEmailModal
          customer={openNotify.customer}
          context={openNotify.context}
          title="RECOVERY EMAIL"
          onClose={() => setOpenNotify(null)}
          onSent={() => setOpenNotify(null)}
        />
      )}
    </div>
  );
}
